import { supabase } from '../lib/supabase';

export const trackingService = {
  // Subscribe to technician location updates for a specific appointment
  subscribeToTechnicianLocation(appointmentId, callback) {
    const channel = supabase?.channel(`appointment-location-${appointmentId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'location_history',
          filter: `appointment_id=eq.${appointmentId}`
        },
        async (payload) => {
          const { data: techData } = await supabase?.from('technicians')?.select('current_latitude, current_longitude, last_location_update')?.eq('id', payload?.new?.technician_id)?.single();

          if (techData) {
            callback({
              latitude: techData?.current_latitude,
              longitude: techData?.current_longitude,
              timestamp: techData?.last_location_update
            });
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  // Subscribe to appointment status changes
  subscribeToAppointmentStatus(appointmentId, callback) {
    const channel = supabase?.channel(`appointment-status-${appointmentId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'appointments',
          filter: `id=eq.${appointmentId}`
        },
        (payload) => {
          callback({
            status: payload?.new?.status,
            estimatedArrivalTime: payload?.new?.estimated_arrival_time,
            actualArrivalTime: payload?.new?.actual_arrival_time,
            completionTime: payload?.new?.completion_time
          });
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  // Subscribe to all technician locations for dispatch center
  subscribeToAllTechnicianLocations(callback) {
    const channel = supabase?.channel('all-technician-locations')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'technicians'
        },
        (payload) => {
          callback({
            technicianId: payload?.new?.id,
            latitude: payload?.new?.current_latitude,
            longitude: payload?.new?.current_longitude,
            status: payload?.new?.status,
            timestamp: payload?.new?.last_location_update
          });
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  // Subscribe to notifications for an appointment
  subscribeToNotifications(appointmentId, callback) {
    const channel = supabase?.channel(`notifications-${appointmentId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `appointment_id=eq.${appointmentId}`
        },
        (payload) => {
          callback(payload?.new);
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  // Get appointment details with technician and location data
  async getAppointmentDetails(appointmentReference) {
    const { data: appointment, error: apptError } = await supabase?.from('appointments')?.select(`
        *,
        technician:technicians (
          id,
          name,
          email,
          phone,
          specialty,
          avatar_url,
          status,
          current_latitude,
          current_longitude,
          last_location_update,
          rating,
          review_count
        )
      `)?.eq('reference', appointmentReference)?.single();

    if (apptError) throw apptError;

    const { data: statusHistory, error: historyError } = await supabase?.from('status_updates')?.select('*')?.eq('appointment_id', appointment?.id)?.order('created_at', { ascending: false });

    if (historyError) throw historyError;

    const { data: notifications, error: notifError } = await supabase?.from('notifications')?.select('*')?.eq('appointment_id', appointment?.id)?.order('created_at', { ascending: false });

    if (notifError) throw notifError;

    return {
      appointment: {
        id: appointment?.id,
        reference: appointment?.reference,
        serviceType: appointment?.service_type,
        urgency: appointment?.urgency,
        status: appointment?.status,
        scheduledDate: appointment?.scheduled_date,
        scheduledTimeStart: appointment?.scheduled_time_start,
        scheduledTimeEnd: appointment?.scheduled_time_end,
        customerName: appointment?.customer_name,
        customerEmail: appointment?.customer_email,
        customerPhone: appointment?.customer_phone,
        customerAddress: appointment?.customer_address,
        customerLatitude: appointment?.customer_latitude,
        customerLongitude: appointment?.customer_longitude,
        estimatedArrivalTime: appointment?.estimated_arrival_time,
        actualArrivalTime: appointment?.actual_arrival_time,
        completionTime: appointment?.completion_time,
        notes: appointment?.notes,
        priceSubtotal: appointment?.price_subtotal,
        priceVat: appointment?.price_vat,
        priceTotal: appointment?.price_total
      },
      technician: appointment?.technician ? {
        id: appointment?.technician?.id,
        name: appointment?.technician?.name,
        email: appointment?.technician?.email,
        phone: appointment?.technician?.phone,
        specialty: appointment?.technician?.specialty,
        avatarUrl: appointment?.technician?.avatar_url,
        status: appointment?.technician?.status,
        currentLatitude: appointment?.technician?.current_latitude,
        currentLongitude: appointment?.technician?.current_longitude,
        lastLocationUpdate: appointment?.technician?.last_location_update,
        rating: appointment?.technician?.rating,
        reviewCount: appointment?.technician?.review_count
      } : null,
      statusHistory: statusHistory?.map((status) => ({
        previousStatus: status?.previous_status,
        newStatus: status?.new_status,
        notes: status?.notes,
        timestamp: status?.created_at
      })),
      notifications: notifications?.map((notif) => ({
        id: notif?.id,
        type: notif?.notification_type,
        title: notif?.title,
        message: notif?.message,
        isRead: notif?.is_read,
        actionable: notif?.actionable,
        timestamp: notif?.created_at
      }))
    };
  },

  // Calculate ETA between technician and customer
  async calculateETA(appointmentId) {
    const { data: appointment, error: apptError } = await supabase?.from('appointments')?.select('customer_latitude, customer_longitude, technician:technicians(current_latitude, current_longitude)')?.eq('id', appointmentId)?.single();

    if (apptError) throw apptError;

    if (!appointment?.technician?.current_latitude || !appointment?.technician?.current_longitude) {
      return null;
    }

    const { data, error } = await supabase?.rpc('calculate_eta', {
      tech_lat: appointment?.technician?.current_latitude,
      tech_lng: appointment?.technician?.current_longitude,
      customer_lat: appointment?.customer_latitude,
      customer_lng: appointment?.customer_longitude
    });

    if (error) throw error;
    return data;
  },

  // Update technician location
  async updateTechnicianLocation(technicianId, location) {
    const { error } = await supabase?.rpc('update_technician_location', {
      tech_id: technicianId,
      new_lat: location?.latitude,
      new_lng: location?.longitude,
      location_accuracy: location?.accuracy || null,
      movement_speed: location?.speed || null,
      movement_heading: location?.heading || null
    });

    if (error) throw error;
  },

  // Update appointment status
  async updateAppointmentStatus(appointmentId, newStatus, notes = null) {
    const { error } = await supabase?.rpc('update_appointment_status', {
      appt_id: appointmentId,
      new_status_value: newStatus,
      status_notes: notes
    });

    if (error) throw error;
  },

  // Get all active appointments for dispatch center
  async getAllActiveAppointments() {
    const { data, error } = await supabase?.from('appointments')?.select(`
        *,
        technician:technicians (
          id,
          name,
          specialty,
          avatar_url,
          status,
          current_latitude,
          current_longitude,
          rating
        )
      `)?.in('status', ['planned', 'confirmed', 'en_route', 'on_site'])?.order('scheduled_date', { ascending: true })?.order('scheduled_time_start', { ascending: true });

    if (error) throw error;

    return data?.map((appt) => ({
      id: appt?.id,
      reference: appt?.reference,
      serviceType: appt?.service_type,
      customerName: appt?.customer_name,
      address: appt?.customer_address,
      scheduledTime: appt?.scheduled_time_start,
      urgency: appt?.urgency,
      status: appt?.status,
      assignedTechnician: appt?.technician?.name,
      technicianId: appt?.technician?.id,
      technicianLocation: appt?.technician ? {
        latitude: appt?.technician?.current_latitude,
        longitude: appt?.technician?.current_longitude
      } : null,
      customerLocation: {
        latitude: appt?.customer_latitude,
        longitude: appt?.customer_longitude
      }
    }));
  },

  // Get all technicians with their current status
  async getAllTechnicians() {
    const { data, error } = await supabase?.from('technicians')?.select('*')?.eq('is_active', true)?.order('name', { ascending: true });

    if (error) throw error;

    return data?.map((tech) => ({
      id: tech?.id,
      name: tech?.name,
      email: tech?.email,
      phone: tech?.phone,
      specialty: tech?.specialty,
      avatarUrl: tech?.avatar_url,
      status: tech?.status,
      currentLocation: tech?.current_latitude && tech?.current_longitude ? {
        latitude: tech?.current_latitude,
        longitude: tech?.current_longitude
      } : null,
      lastLocationUpdate: tech?.last_location_update,
      rating: tech?.rating,
      reviewCount: tech?.review_count,
      maxDailyServices: tech?.max_daily_services
    }));
  },

  // Mark notification as read
  async markNotificationAsRead(notificationId) {
    const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId);

    if (error) throw error;
  },

  // Get notification preferences for a customer
  async getNotificationPreferences(customerEmail) {
    const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('customer_email', customerEmail)?.single();

    if (error && error?.code !== 'PGRST116') throw error;

    if (!data) {
      return {
        enableProximityAlerts: true,
        enable15minAlert: true,
        enable10minAlert: true,
        enable5minAlert: true,
        enableArrivalAlert: true,
        enableCompletionAlert: true,
        enableSms: false,
        enableEmail: true,
        enablePush: true
      };
    }

    return {
      enableProximityAlerts: data?.enable_proximity_alerts,
      enable15minAlert: data?.enable_15min_alert,
      enable10minAlert: data?.enable_10min_alert,
      enable5minAlert: data?.enable_5min_alert,
      enableArrivalAlert: data?.enable_arrival_alert,
      enableCompletionAlert: data?.enable_completion_alert,
      enableSms: data?.enable_sms,
      enableEmail: data?.enable_email,
      enablePush: data?.enable_push
    };
  },

  // Update notification preferences
  async updateNotificationPreferences(customerEmail, preferences) {
    const { error } = await supabase?.from('notification_preferences')?.upsert({
      customer_email: customerEmail,
      enable_proximity_alerts: preferences?.enableProximityAlerts,
      enable_15min_alert: preferences?.enable15minAlert,
      enable_10min_alert: preferences?.enable10minAlert,
      enable_5min_alert: preferences?.enable5minAlert,
      enable_arrival_alert: preferences?.enableArrivalAlert,
      enable_completion_alert: preferences?.enableCompletionAlert,
      enable_sms: preferences?.enableSms,
      enable_email: preferences?.enableEmail,
      enable_push: preferences?.enablePush,
      updated_at: new Date()?.toISOString()
    }, {
      onConflict: 'customer_email'
    });

    if (error) throw error;
  },

  // Trigger proximity notification check (called by cron or manually)
  async triggerProximityCheck() {
    const { data, error } = await supabase?.functions?.invoke('process-proximity-notifications');

    if (error) throw error;
    return data;
  }
};